import type { Id } from "../../convex/_generated/dataModel";

// Shared image upload pipeline (plan todo 13), extracted from Chat.tsx so the
// chat composer AND the photo logging flow (PhotoLogFlow) use ONE upload path.
// Pipeline order is contractual: validate limits -> sha256 -> generateUploadUrl
// -> POST raw file -> claimUpload -> local object-URL preview.
//
// WebView safety: never use crypto.randomUUID here (non-HTTPS WebViews crash);
// temp ids elsewhere use the Date.now()+Math.random pattern.

export type ImageUploadState =
  | { readonly phase: "idle" }
  | {
      readonly phase: "uploading";
      readonly file: File;
      readonly fileName: string;
      readonly attempt: number;
    }
  | {
      readonly phase: "error";
      readonly file: File;
      readonly fileName: string;
      readonly attempt: number;
      readonly message: string;
    };

export function beginImageUpload(file: File, attempt = 1): ImageUploadState {
  return { phase: "uploading", file, fileName: file.name, attempt };
}

export function failImageUpload(
  state: ImageUploadState,
  message: string,
): ImageUploadState {
  if (state.phase !== "uploading") return state;
  return {
    phase: "error",
    file: state.file,
    fileName: state.fileName,
    attempt: state.attempt,
    message,
  };
}

export function canRetryImageUpload(state: ImageUploadState): boolean {
  return state.phase === "error";
}

export function retryImageUpload(state: ImageUploadState): ImageUploadState {
  if (state.phase !== "error") return state;
  return beginImageUpload(state.file, state.attempt + 1);
}

export interface ImageUploadLimits {
  readonly maxBytes: number;
  readonly allowedTypes: readonly string[];
}

// Returns the user-facing rejection message, or null when the file passes.
// Messages are contractual (Chat alerts with them verbatim).
export function imageFileValidationError(
  file: File,
  limits: ImageUploadLimits,
): string | null {
  if (file.size > limits.maxBytes) {
    return `Image too large. Max ${limits.maxBytes / (1024 * 1024)}MB.`;
  }
  if (!limits.allowedTypes.some((type) => type === file.type)) {
    return "Invalid image type. Please use jpeg, png, or webp.";
  }
  return null;
}

export async function sha256Hex(file: File): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    await file.arrayBuffer(),
  );
  return Array.from(new Uint8Array(digest), (byte) =>
    byte.toString(16).padStart(2, "0"),
  ).join("");
}

export async function uploadImageFile(
  uploadUrl: string,
  file: File,
): Promise<Id<"_storage">> {
  const response = await fetch(uploadUrl, {
    method: "POST",
    headers: { "Content-Type": file.type },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
  }
  const json: unknown = await response.json();
  if (
    typeof json !== "object" ||
    json === null ||
    !("storageId" in json) ||
    typeof json.storageId !== "string"
  ) {
    throw new Error("Upload response missing storageId");
  }
  return json.storageId as Id<"_storage">;
}

export interface ImageUploadMutations {
  readonly generateUploadUrl: (args: {
    expectedSha256: string;
  }) => Promise<{ uploadUrl: string; claimToken: string }>;
  readonly claimUpload: (args: {
    storageId: Id<"_storage">;
    claimToken: string;
  }) => Promise<unknown>;
}

export interface UploadedImage {
  readonly storageId: Id<"_storage">;
  // Local object URL for the preview thumbnail. The CALLER owns revocation
  // (URL.revokeObjectURL) when the preview is replaced or the flow unmounts.
  readonly previewUrl: string;
}

// Full upload pipeline shared by Chat and PhotoLogFlow. `createPreviewUrl` is
// injectable because URL.createObjectURL does not exist in the node test env.
export async function uploadImagePipeline(
  file: File,
  mutations: ImageUploadMutations,
  createPreviewUrl: (file: File) => string = (target) => URL.createObjectURL(target),
): Promise<UploadedImage> {
  const expectedSha256 = await sha256Hex(file);
  const { uploadUrl, claimToken } = await mutations.generateUploadUrl({ expectedSha256 });
  const storageId = await uploadImageFile(uploadUrl, file);
  await mutations.claimUpload({ storageId, claimToken });
  return { storageId, previewUrl: createPreviewUrl(file) };
}
